import React, { memo } from 'react';
import { Control, Controller } from 'react-hook-form';
import Typography from '../../../typography/typography';
import Radio from '../../../radio/radio';
import { FormControlLabel, RadioGroup, Box } from '@mui/material';
import { styled } from '@mui/material';

interface IDynamicRadioButton {
	formControl: Control<any>;
	fieldArrayName: string;
	name: string;
	label: string;
	titlePosition?: string;
	position: number;
	options: {
		label: string;
		value: string;
	}[];
	disabled?: boolean;
	typeOfField?: string;
	display?: 'row' | 'column';
	title?: string;
	bordered?: boolean;
	defaultValue?: string; // Optional defaultValue prop
}

const BorderedOption = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'bordered' && prop !== 'isSelected'
})<{ bordered?: boolean; isSelected: boolean }>(({ theme, bordered }) => ({
	display: 'flex',
	alignItems: 'center',
	padding: 0,
	margin: 0,
	...(bordered && {
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.shape.borderRadius,
		margin: theme.spacing(0, 3, 0, 0),
		padding: theme.spacing(0, 5, 0, 1),
		height: '36px',
		alignContent: 'left',
		'&:hover': {
			backgroundColor: theme.palette.action.hover
		}
	})
}));

export const DynamicRadioButton: React.FC<IDynamicRadioButton> = memo(
	({
		formControl,
		fieldArrayName,
		name,
		options,
		display,
		label,
		position,
		disabled,
		title,
		titlePosition,
		bordered = false,
		defaultValue = ''
	}) => {
		return (
			<>
				<Typography
					type='s5'
					weight='medium'
					color='theme.secondary.800'
					data-position={position}
					sx={{ mb: 0.5 }}>
					{label}
				</Typography>
				<Controller
					control={formControl}
					name={`${fieldArrayName}.${name}`}
					// disabled={disabled}
					defaultValue={defaultValue} // Set defaultValue to 'item' if none provided
					render={({ field, fieldState: { error } }) => (
						<>
							<RadioGroup
								value={field.value ?? defaultValue} // Ensure 'item' if value is undefined
								onChange={(e) => field.onChange(e.target.value)}
								row={display === 'row'}
								sx={{ alignItems: 'center', gap: 1 }}>
								{title && titlePosition === 'start' && (
									<Typography
										type='s4'
										color='theme.secondary.800'
										weight='medium'>
										{title}
									</Typography>
								)}
								{options.map((option) => (
									<BorderedOption
										key={option.value}
										bordered={bordered}
										isSelected={field.value == option.value}>
										<FormControlLabel
											sx={{ margin: 0, opacity: disabled ? 0.5 : 1 }}
											value={option.value}
											control={<Radio checked={field.value == option.value} />}
											label={
												<Typography
													type='s5'
													weight='medium'
													color='theme.secondary.800'>
													{option.label}
												</Typography>
											}
											disabled={disabled}
										/>
									</BorderedOption>
								))}
								{title && titlePosition === 'end' && (
									<Typography
										type='s4'
										color='theme.secondary.800'
										weight='medium'>
										{title}
									</Typography>
								)}
							</RadioGroup>
							{error && (
								<Typography type='s5' color='theme.error.600'>
									{error.message}
								</Typography>
							)}
						</>
					)}
				/>
			</>
		);
	}
);

export default DynamicRadioButton;
